// frontend/src/contexts/WebSocketContext.jsx
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { getAccessToken, tryRefresh } from "../api/client.js";
import { isTokenExpiringSoon } from "../lib/jwt.js";
import { isNative } from "../lib/platform.js";
import { createWsLogger } from "../lib/wsDebugLog.js";
import { SYNC_ABSENCE_THRESHOLD_MS } from "../config/syncAnimation.js";

const wsLog = createWsLogger("[ws]");

// Stale-OPEN probe: al volver el tab con un socket aparentemente OPEN, se manda
// un ping app-level; si el pong no llega en este plazo, el socket está muerto
// del lado del backend (keepalive terminate durante freeze) y se fuerza reconnect.
const STALE_OPEN_PONG_TIMEOUT_MS = 5000;

// Watchdog del handshake. Un WebSocket puede quedarse en CONNECTING por
// MINUTOS: la radio del teléfono todavía sin datos al volver de background
// (SYN al vacío → timeout TCP de la plataforma, 60–120 s), DNS que no
// resuelve, o el upgrade colgado en un backend saturado. Durante todo ese
// tiempo el dedup de connect() y checkAndRevive ven un socket "en curso" y no
// actúan — era EL cuelgue de la app nativa (salir y volver no lo destrababa;
// solo matar la app). Pasado este plazo el intento se descarta y se re-disca.
const CONNECT_TIMEOUT_MS = 10_000;

// Recorte del backoff con la pestaña visible: el usuario está mirando, y
// esperar el escalón de 30 s con la red ya de vuelta se percibe como "quedó
// pegado". Solo aplica los PRIMEROS intentos (ver VISIBLE_CAP_MAX_ATTEMPTS):
// pasada esa ventana el banner ya está a la vista con su botón de reintento —
// la inmediatez la pone el usuario — y el escalón de 30 s vuelve a espaciar
// los handshakes contra un backend que probablemente está caído (cada intento
// le cuesta TLS + verificación de JWT + query de revocación).
const VISIBLE_MAX_RECONNECT_DELAY_MS = 10_000;
const VISIBLE_CAP_MAX_ATTEMPTS = 8; // ~1 min de reintentos rápidos

// Sin red no se disca: el intento naufraga sí o sí (o peor: queda CONNECTING
// hasta el timeout TCP) y solo infla el backoff. El evento `online` reconecta
// en el acto; este re-chequeo es solo el fallback para WebViews que pierdan el
// evento, así que puede ser espaciado.
const OFFLINE_RECHECK_MS = 10_000;

// Al llegar `online` con un dial CONNECTING en vuelo: un dial recién nacido
// puede estar discando ya contra la red nueva — y los navegadores emiten
// online en ráfagas (portal cautivo, wifi débil); matarlo en cada evento
// impediría que NINGÚN handshake complete. Solo se descarta el que lleva rato
// (empezó contra la red vieja o sin red).
const ONLINE_DISCARD_CONNECTING_AGE_MS = 1500;

// Piso entre refrescos de token disparados "a ciegas" por la racha de
// handshakes fallidos: en el mundo cookie-only (desktop) el exp no se puede
// leer y sin piso se golpearía /api/auth/refresh en cada intento del backoff.
const TOKEN_REFRESH_MIN_INTERVAL_MS = 60_000;

function resolveWsUrl() {
  if (import.meta.env.VITE_WS_URL) return import.meta.env.VITE_WS_URL;
  if (import.meta.env.VITE_API_URL) {
    return (
      import.meta.env.VITE_API_URL.replace(/^https:\/\//, "wss://")
        .replace(/^http:\/\//, "ws://")
        .replace(/\/$/, "") + ":8080"
    );
  }
  return `ws://${window.location.hostname}:8080`;
}

const WS_BASE_URL = resolveWsUrl();

/**
 * Build the WS connection URL.
 * On desktop/Firefox where httpOnly cookies work, the upgrade request carries
 * the access_token cookie automatically — no extra param needed.
 * On iOS Safari (ITP) and incognito mode cross-site cookies are blocked, so
 * we append the in-memory access token as ?t=<jwt>. The backend verifyWsToken
 * accepts cookie OR query param, preferring the cookie.
 *
 * Called at connect-time (not memoized) so every reconnect attempt reads the
 * latest token — important after a silent token refresh mid-session.
 */
function buildWsUrl() {
  const token = getAccessToken();
  return token ? `${WS_BASE_URL}?t=${token}` : WS_BASE_URL;
}

const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 30000];

// Jitter sobre el escalón de backoff: 50–100% de la base. Sin esto, todas las
// pestañas que perdieron el socket en el mismo instante (deploy, reinicio del
// backend) reconectan sincronizadas y golpean el server en oleada.
// ÚNICA fuente de la política de delay (tabla + cap + jitter): el cap visible
// pasa por acá y no por una fórmula paralela. Exportada para poder fijar
// `rand` en tests.
export function computeReconnectDelay(
  attempt,
  rand = Math.random(),
  { capMs = null } = {},
) {
  const idx = Math.min(attempt, RECONNECT_DELAYS_MS.length - 1);
  let base = RECONNECT_DELAYS_MS[idx];
  if (capMs != null && base > capMs) base = capMs;
  return Math.round(base * (0.5 + rand * 0.5));
}

// Heartbeat periódico app-level (el browser no expone los ping frames nativos
// del protocolo). Detecta sockets muertos en conexiones "estables" — radio
// móvil que soltó el TCP sin FIN — sin esperar a que el usuario cambie de
// pestaña. El servidor ya corre su keepalive de protocolo cada 30 s; este es
// el equivalente del lado cliente. En background el intervalo se estrangula a
// ~1 tick/min: suficiente, la meta es detectar en minutos, no en milisegundos.
const HEARTBEAT_INTERVAL_MS = 30_000;
// Plazo de pong para el heartbeat automático. Más laxo que el de la vuelta de
// pestaña: acá nadie está esperando y un falso positivo manda a toda la flota
// de pestañas a reconectar contra un backend que quizá solo estaba lento.
const HEARTBEAT_PONG_TIMEOUT_MS = 10_000;

// Coalescing de mensajes entrantes: cada onmessage llega en su propio macrotask,
// así que sin buffer cada mensaje de posición commitea un render completo del
// árbol. Acumulando y drenando el lote en un solo tick, React agrupa los
// setState de todos los mensajes pendientes en un único commit por frame.
// Con la pestaña oculta rAF no dispara, así que ahí se drena por timeout (el
// navegador lo estira a ~1s en background; el lote crece y se procesa junto).
const FLUSH_FALLBACK_MS = 250;
// Válvula de seguridad: si el navegador estrangula los timers mucho más de lo
// esperado (intensive throttling: ~1 disparo/min), drenar sincrónicamente antes
// de que el buffer retenga memoria. Dimensionada para ser alcanzable de verdad:
// ~1 min de backlog de una flota grande.
const MAX_PENDING_MESSAGES = 1000;

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const subscribersRef = useRef(new Set());
  const reconnectHandlersRef = useRef(new Set());
  const wsRef = useRef(null);
  const mountedRef = useRef(true);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const wasConnectedRef = useRef(false);
  const tabHiddenAtRef = useRef(null); // null when visible; Date.now() when hidden
  // Ausencia CONGELADA al volver (now − tabHiddenAt del ciclo que terminó).
  // Congelar al regreso y no al consumir importa: la marca de un alt-tab corto
  // no se consume (no dispara resync), y si el cálculo fuera al consumir, una
  // reconexión media hora después leería "30 min de ausencia" y colapsaría las
  // trazas de toda la flota aunque el usuario estuvo mirando el mapa.
  const lastAbsenceMsRef = useRef(null);
  const pingTimeoutRef = useRef(null); // pending stale-OPEN probe timeout
  // Resync tab-return diferido a la BARRERA DE PONG (ver handleAppReturn).
  // Guarda el INSTANTE en que se armó (null = sin barrera): solo un pong cuyo
  // echoTs sea >= a este instante la libera. Un pong de un ping anterior al
  // freeze NO viaja detrás del backlog encolado — liberarla con él re-abriría
  // exactamente la carrera que la barrera cierra.
  const pendingTabReturnSinceRef = useRef(null);
  const pendingMessagesRef = useRef([]);
  // Último byte recibido por el socket actual. Cualquier mensaje entrante ya
  // prueba que el socket está vivo, así que el heartbeat no tiene nada que
  // averiguar mientras la flota reporte: con esto el ping deja de mandarse por
  // completo en operación normal y solo aparece cuando el socket se calló, que
  // es el único caso que el probe quiere detectar.
  const lastInboundAtRef = useRef(0);
  const flushHandleRef = useRef(null); // { raf } | { timer } | null
  // Watchdog del handshake en curso + timestamp del intento: checkAndRevive lo
  // usa para distinguir un CONNECTING legítimo (joven) de uno que quedó
  // congelado (los timers no corren durante un freeze, así que el watchdog
  // armado puede no haber disparado nunca).
  const connectWatchdogRef = useRef(null);
  const connectStartedAtRef = useRef(0);
  // Cierres consecutivos SIN llegar a abrir Y RÁPIDOS (handshake rechazado —
  // 401 típico). Dispara el refresh de token "a ciegas" del próximo dial. Los
  // atascos de CONNECTING que descarta el watchdog NO cuentan: son evidencia
  // de red, no de credencial.
  const neverOpenedStreakRef = useRef(0);
  const lastRefreshKickRef = useRef(0);
  const [connected, setConnected] = useState(false);

  const subscribe = useCallback((fn) => subscribersRef.current.add(fn), []);
  const unsubscribe = useCallback(
    (fn) => subscribersRef.current.delete(fn),
    [],
  );
  const onReconnect = useCallback(
    (fn) => reconnectHandlersRef.current.add(fn),
    [],
  );
  const offReconnect = useCallback(
    (fn) => reconnectHandlersRef.current.delete(fn),
    [],
  );

  const sendMessage = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const cancelPendingFlush = useCallback(() => {
    const handle = flushHandleRef.current;
    if (!handle) return;
    if (handle.raf != null) cancelAnimationFrame(handle.raf);
    if (handle.timer != null) clearTimeout(handle.timer);
    flushHandleRef.current = null;
  }, []);

  const flushPendingMessages = useCallback(() => {
    flushHandleRef.current = null;
    const batch = pendingMessagesRef.current;
    if (batch.length === 0) return;
    pendingMessagesRef.current = [];
    // Entrega en orden de llegada, todo dentro del mismo tick: React agrupa
    // los setState de todo el lote en un solo commit. Cada handler va aislado:
    // sin el try/catch, un suscriptor que lanza descartaría el resto del lote
    // (el buffer ya se vació arriba). Antes del coalescer un throw costaba
    // exactamente un mensaje; mantener esa garantía.
    for (const msg of batch) {
      for (const fn of subscribersRef.current) {
        try {
          fn(msg);
        } catch (err) {
          wsLog("subscriber threw during flush", { err: String(err) });
          console.error("[ws] subscriber error:", err);
        }
      }
    }
  }, []);

  const schedulePendingFlush = useCallback(() => {
    if (flushHandleRef.current) return;
    if (document.visibilityState === "visible") {
      flushHandleRef.current = {
        raf: requestAnimationFrame(flushPendingMessages),
      };
    } else {
      flushHandleRef.current = {
        timer: setTimeout(flushPendingMessages, FLUSH_FALLBACK_MS),
      };
    }
  }, [flushPendingMessages]);

  // Fan-out del "resincronizá ahora" a los consumidores (useDevices). Se
  // dispara en DOS situaciones, no solo una:
  //   - "reconnect": el socket se cayó y volvió (onopen con isReconnect).
  //   - "tab-return": la pestaña volvió tras una ausencia larga con el socket
  //     SANO (diferido a la barrera de pong — ver handleAppReturn). Sin este
  //     caso el resync nunca corría en una conexión estable (checkAndRevive
  //     solo reconecta si el socket está CLOSED/CLOSING), así que al volver no
  //     se re-fetcheaban posiciones ni se evaluaba el sync.
  const fireResyncHandlers = useCallback((reason) => {
    // Cualquier resync que arranque supersede a un tab-return pendiente de
    // barrera: su refetch ya trae lo que aquel iba a buscar.
    pendingTabReturnSinceRef.current = null;
    // Drenar el buffer ANTES de que los handlers capturen su startTs: los
    // mensajes acumulados llegaron antes de este resync y deben aplicarse (y
    // versionarse) antes del refetch, no aterrizar encima de él. Sin esto, un
    // flush tardío estampa versiones > startTs y el merge preserva estado
    // viejo descartando el snapshot HTTP fresco.
    cancelPendingFlush();
    flushPendingMessages();
    const handlers = Array.from(reconnectHandlersRef.current);
    wsLog("fire resync handlers", { reason, count: handlers.length });
    Promise.allSettled(
      handlers.map((fn) => {
        try {
          return Promise.resolve(fn(reason));
        } catch (err) {
          return Promise.reject(err);
        }
      }),
    ).then((results) => {
      results.forEach((r, i) => {
        if (r.status === "rejected")
          wsLog(`resyncHandler[${i}] rejected`, { reason, err: String(r.reason) });
      });
    });
  }, [cancelPendingFlush, flushPendingMessages]);

  // Read-and-clear: la ausencia del ciclo hidden→visible más reciente. Si la
  // pestaña SIGUE oculta (reconexión en background), se calcula en vivo; si ya
  // volvió, se entrega el valor congelado al regreso (ver lastAbsenceMsRef).
  // Stable identity (useCallback sin deps) para capturarla en closures.
  const getAndConsumeLastAbsenceMs = useCallback(() => {
    if (tabHiddenAtRef.current != null) {
      const elapsed = Date.now() - tabHiddenAtRef.current;
      tabHiddenAtRef.current = null;
      lastAbsenceMsRef.current = null;
      return elapsed;
    }
    if (lastAbsenceMsRef.current != null) {
      const value = lastAbsenceMsRef.current;
      lastAbsenceMsRef.current = null;
      return value;
    }
    return null;
  }, []);

  // Nullify listeners de un socket descartado para que sus eventos tardíos
  // (onclose del viejo llegando después del onopen del nuevo) no toquen estado.
  const detachSocket = (ws) => {
    ws.onmessage = null;
    ws.onopen = null;
    ws.onerror = null;
    ws.onclose = null;
  };

  const clearPingTimeout = () => {
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
      pingTimeoutRef.current = null;
    }
  };

  const clearConnectWatchdog = () => {
    if (connectWatchdogRef.current) {
      clearTimeout(connectWatchdogRef.current);
      connectWatchdogRef.current = null;
    }
  };

  // Núcleo compartido de "descartar un socket": TODOS los caminos que retiran
  // un socket (watchdog, CONNECTING congelado, vuelta de red, probe sin pong,
  // reintento manual) pasan por acá para que ninguno olvide un timer o el
  // detach — cada recurso per-socket nuevo se limpia en UN lugar. El caller
  // conserva solo su semántica propia (setConnected, racha, redial).
  const discardSocket = (ws) => {
    clearConnectWatchdog();
    if (wsRef.current === ws) wsRef.current = null;
    detachSocket(ws);
    try {
      ws.close();
    } catch {
      // ignore
    }
  };

  const connect = useCallback((opts = {}) => {
    if (!mountedRef.current) return;
    // Dedup: si ya hay un socket vivo o abriéndose, no crear uno paralelo
    // (zombie). Los paths muertos (CLOSED/CLOSING/null) sí pasan.
    const existing = wsRef.current;
    if (
      existing &&
      (existing.readyState === WebSocket.CONNECTING ||
        existing.readyState === WebSocket.OPEN)
    ) {
      wsLog("connect() skipped — existing socket alive", { readyState: existing.readyState });
      return;
    }

    // Sin red el dial naufraga sí o sí (ver OFFLINE_RECHECK_MS). onLine=false
    // es confiable en general — pero WebView/VPN tienen falsos negativos
    // conocidos, así que el reintento manual (forceReconnect) pasa con
    // ignoreOffline: el gesto del usuario es la última salida y no puede
    // terminar en un defer silencioso.
    if (
      !opts.ignoreOffline &&
      typeof navigator !== "undefined" &&
      navigator.onLine === false
    ) {
      wsLog("connect() deferred — offline", {});
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(connect, OFFLINE_RECHECK_MS);
      return;
    }

    wsLog("connect() called", { readyState: existing?.readyState ?? null, wasConnected: wasConnectedRef.current });

    // Refresh del token SIN bloquear el dial: si el token en memoria ya venció,
    // este intento va a fallar con un 401 barato y kickTokenRefresh rediscará
    // apenas tenga el token nuevo. Bloquear el dial a la espera del refresh
    // volvería connect() async y abriría carreras con el dedup.
    if (
      isTokenExpiringSoon(getAccessToken()) ||
      neverOpenedStreakRef.current >= 2
    ) {
      kickTokenRefresh({ redialOnSuccess: true });
    }

    const ws = new WebSocket(buildWsUrl());
    wsRef.current = ws;
    // Clausura local, no expando sobre el objeto de plataforma: onopen/onclose
    // son clausuras de ESTA llamada sobre este mismo `ws`.
    let everOpened = false;

    // Reintento con backoff, compartido por onclose y el watchdog. La política
    // (tabla + cap visible + jitter) vive entera en computeReconnectDelay.
    const scheduleReconnect = () => {
      const visible =
        typeof document !== "undefined" &&
        document.visibilityState === "visible";
      const capMs =
        visible && reconnectAttemptRef.current < VISIBLE_CAP_MAX_ATTEMPTS
          ? VISIBLE_MAX_RECONNECT_DELAY_MS
          : null;
      const delay = computeReconnectDelay(reconnectAttemptRef.current, undefined, {
        capMs,
      });
      reconnectAttemptRef.current += 1;
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    // Watchdog de CONNECTING (ver CONNECT_TIMEOUT_MS): si el handshake no
    // resolvió en el plazo, el intento se descarta y se re-disca con backoff.
    // NO alimenta la racha de refresh: un CONNECTING atascado es evidencia de
    // RED (radio dormida, SYN al vacío), no de credencial — contarlo acá
    // dispararía refreshes de token por cada corte de radio.
    connectStartedAtRef.current = Date.now();
    clearConnectWatchdog();
    connectWatchdogRef.current = setTimeout(() => {
      connectWatchdogRef.current = null;
      if (ws !== wsRef.current || ws.readyState === WebSocket.OPEN) return;
      wsLog("connect watchdog — CONNECTING stuck, discarding", {
        readyState: ws.readyState,
      });
      discardSocket(ws);
      setConnected(false);
      if (mountedRef.current) scheduleReconnect();
    }, CONNECT_TIMEOUT_MS);

    ws.onopen = () => {
      if (ws !== wsRef.current) return;
      clearConnectWatchdog();
      everOpened = true;
      neverOpenedStreakRef.current = 0;
      reconnectAttemptRef.current = 0;
      // Arranca el reloj de silencio del socket nuevo: sin esto el primer tick
      // del heartbeat compararía contra la actividad del socket anterior.
      lastInboundAtRef.current = Date.now();
      const isReconnect = wasConnectedRef.current;
      wasConnectedRef.current = true;
      wsLog("onopen", { ts: Date.now(), reconnectHandlers: reconnectHandlersRef.current.size, isReconnect });
      setConnected(true);
      if (isReconnect) fireResyncHandlers("reconnect");
    };

    ws.onmessage = (event) => {
      // Antes del parse: hasta un frame malformado prueba que el socket vive.
      lastInboundAtRef.current = Date.now();
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return; // ignore malformed messages
      }
      if (msg?.type === "pong") {
        wsLog("pong received", { echoTs: msg.echoTs ?? null });
        // Cualquier pong prueba vida: el probe se desarma siempre.
        clearPingTimeout();
        // Barrera de pong (ver handleAppReturn): el stream WS es FIFO, así
        // que un pong POSTERIOR al regreso viajó DETRÁS de todo el backlog
        // que el kernel tuviera encolado del freeze — ya se entregó y está en
        // el coalescer; un resync disparado ahora versiona después de él y el
        // snapshot HTTP fresco gana como corresponde. El echoTs distingue ese
        // pong del de un ping ANTERIOR al freeze (que llega DELANTE del
        // backlog y no prueba nada): solo libera la barrera un eco igual o
        // posterior a su armado.
        const barrierArmedAt = pendingTabReturnSinceRef.current;
        if (
          barrierArmedAt != null &&
          typeof msg.echoTs === "number" &&
          msg.echoTs >= barrierArmedAt
        ) {
          pendingTabReturnSinceRef.current = null;
          fireResyncHandlers("tab-return");
        }
        return;
      }
      // Momento de LLEGADA, no de entrega: los consumidores que versionan
      // estado contra "cuándo llegó esto" (useDevices: deviceWsVersionRef,
      // watchdog de silencio) deben leer esta marca — el flush puede correr
      // hasta ~1 min después con la pestaña oculta.
      if (msg && typeof msg === "object") msg.__wsReceivedAt = Date.now();
      pendingMessagesRef.current.push(msg);
      if (pendingMessagesRef.current.length >= MAX_PENDING_MESSAGES) {
        cancelPendingFlush();
        flushPendingMessages();
        return;
      }
      schedulePendingFlush();
    };

    ws.onclose = (event) => {
      const isCurrent = ws === wsRef.current;
      wsLog("onclose", { isCurrent, code: event.code, wasClean: event.wasClean });
      if (!isCurrent) {
        detachSocket(ws);
        return;
      }
      clearConnectWatchdog();
      clearPingTimeout();
      // Cerró sin haber llegado a abrir: handshake rechazado (401 de token
      // vencido, el caso típico) o red rota. Alimenta la racha que dispara el
      // refresh del token en el próximo dial.
      if (!everOpened) neverOpenedStreakRef.current += 1;
      if (event.code === 4401) {
        // El backend cerró por revocación (logout remoto, cambio de rol —
        // closeMatching/catchUpSweep en ws/server.js). El access token en
        // memoria ya no sirve: refrescar YA, sin piso. Con un refresh token
        // vigente (cambio de rol) la sesión se recompone sola y se redisca;
        // si también está revocado, el próximo request HTTP dispara el
        // logout global (_onUnauthorized) y desmonta este provider.
        kickTokenRefresh({ force: true, redialOnSuccess: true });
      }
      // Drenar lo que llegó por este socket antes de morir: si quedara en el
      // buffer, un timer estrangulado lo entregaría DESPUÉS del resync de la
      // reconexión, revirtiendo estado (device_status/command_update no tienen
      // guard de versión). Entregado ahora, el refetch posterior lo corrige.
      cancelPendingFlush();
      flushPendingMessages();
      // Marcar el socket actual como muerto ANTES de programar el reconnect,
      // así el dedup de connect() nunca ve un cadáver como "existing".
      wsRef.current = null;
      setConnected(false);
      if (mountedRef.current) {
        scheduleReconnect();
      }
    };

    ws.onerror = () => {
      if (ws !== wsRef.current) {
        detachSocket(ws);
        return;
      }
      ws.close();
    };
  }, [
    fireResyncHandlers,
    cancelPendingFlush,
    flushPendingMessages,
    schedulePendingFlush,
  ]);

  // Refresh de token single-flight con pacing propio. `tryRefresh` ya dedupea
  // llamadas concurrentes; el piso de 60 s evita ADEMÁS martillar
  // /api/auth/refresh una vez por escalón del backoff cuando el exp no se
  // puede leer (cookie httpOnly) y la racha lo dispara a ciegas. `force`
  // (cierre 4401, reintento manual) lo saltea. Con `redialOnSuccess`, un
  // refresh que triunfa corta el backoff en curso y disca ya: sin esto, el
  // token fresco esperaba sentado hasta 8-30 s a que venciera el timer.
  // Declarada como function para poder referenciarla desde connect() (arriba)
  // sin ceremonia de refs — se llama siempre post-init.
  function kickTokenRefresh({ force = false, redialOnSuccess = false } = {}) {
    if (
      !force &&
      Date.now() - lastRefreshKickRef.current < TOKEN_REFRESH_MIN_INTERVAL_MS
    ) {
      return;
    }
    lastRefreshKickRef.current = Date.now();
    wsLog("kick token refresh", {
      force,
      redialOnSuccess,
      streak: neverOpenedStreakRef.current,
    });
    tryRefresh()
      .then((ok) => {
        if (!ok || !redialOnSuccess || !mountedRef.current) return;
        const ws = wsRef.current;
        if (
          ws &&
          (ws.readyState === WebSocket.CONNECTING ||
            ws.readyState === WebSocket.OPEN)
        ) {
          return; // ya hay un dial/socket en vuelo; que siga
        }
        wsLog("token refreshed — redialing now");
        clearTimeout(reconnectTimerRef.current);
        connect();
      })
      .catch(() => {});
  }

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimerRef.current);
      clearPingTimeout();
      clearConnectWatchdog();
      cancelPendingFlush();
      pendingMessagesRef.current = [];
      wsRef.current?.close();
    };
  }, [connect, cancelPendingFlush]);

  // Probe de vida app-level sobre el socket actual: ping; si no llega pong en
  // el plazo, el socket está muerto del lado del backend (keepalive terminate
  // durante un freeze, radio móvil que soltó el TCP sin FIN) — matarlo y
  // reconectar. No apila: si ya hay un probe en vuelo, no-op.
  //
  // Los dos disparadores NO son equivalentes y por eso parametrizan el probe:
  //
  //   - Vuelta de pestaña (`checkAndRevive`): evento del usuario, esporádico y
  //     con alta probabilidad real de socket muerto tras el freeze. Plazo
  //     corto — queremos volver YA.
  //   - Heartbeat periódico: corre solo, cada 30 s, en cada pestaña abierta.
  //     Acá un falso positivo es caro: si el backend está lento (cold start de
  //     Fly, reporte pesado) o el RTT móvil se dispara, matar un socket sano
  //     manda a TODAS las pestañas a reconectar y a pedir el resync completo
  //     contra el servidor que ya estaba sufriendo. Por eso el plazo es más
  //     laxo: el ciclo kill→reconnect→open no lo frena el backoff (`onopen`
  //     resetea el contador en cuanto el socket abre, que es justo lo que pasa
  //     acá), así que la única defensa real es no declarar muerto de más.
  const probeAliveness = useCallback(
    ({ timeoutMs = STALE_OPEN_PONG_TIMEOUT_MS } = {}) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || pingTimeoutRef.current)
        return;
      wsLog("probing socket liveness with ping", { timeoutMs });
      try {
        ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
      } catch {
        // send puede lanzar si el socket murió entre el check y el send
      }
      pingTimeoutRef.current = setTimeout(() => {
        pingTimeoutRef.current = null;
        // Si otro path (CLOSED branch, onclose) ya reemplazó o descartó el
        // socket probeado, no tocar nada: anular wsRef aquí dejaría huérfano
        // a un socket nuevo sano (zombie + mensajes duplicados).
        if (ws !== wsRef.current) return;
        wsLog("stale socket detected (no pong) — forcing reconnect");
        discardSocket(ws);
        setConnected(false);
        reconnectAttemptRef.current = 0;
        connect();
      }, timeoutMs);
    },
    [connect],
  );

  // Heartbeat periódico mientras el socket figura conectado.
  useEffect(() => {
    if (!connected) return undefined;
    const timer = setInterval(() => {
      // Si el socket entregó algo dentro del último intervalo, ya está probado
      // vivo: pinguear sería 2 frames por pestaña cada 30 s (≈5.700/día) que
      // no aportan información, despertando el event loop del backend en cada
      // uno. Con la flota reportando, el ping directamente no se manda.
      if (Date.now() - lastInboundAtRef.current < HEARTBEAT_INTERVAL_MS) return;
      probeAliveness({ timeoutMs: HEARTBEAT_PONG_TIMEOUT_MS });
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [connected, probeAliveness]);

  // Revive el socket según su estado real. Compartido por todos los caminos de
  // "la app volvió": visibilitychange, pageshow (BFCache), resume (Page
  // Lifecycle), appStateChange (Capacitor) y el evento online.
  const checkAndRevive = useCallback(() => {
    const ws = wsRef.current;
    if (
      !ws ||
      ws.readyState === WebSocket.CLOSED ||
      ws.readyState === WebSocket.CLOSING
    ) {
      // Un probe puede seguir armado sobre un socket cuyo readyState ya es
      // CLOSED pero cuyo evento close aún no despachó — limpiarlo para que
      // no dispare sobre el reemplazo que vamos a crear.
      clearPingTimeout();
      clearConnectWatchdog();
      clearTimeout(reconnectTimerRef.current);
      reconnectAttemptRef.current = 0; // user-driven event, not error retry
      connect();
    } else if (ws.readyState === WebSocket.OPEN) {
      // Stale-OPEN probe: tras un freeze el backend puede haber hecho
      // terminate() por keepalive mientras el cliente aún cree que el socket
      // está OPEN. probeAliveness pinguea y, sin pong en 5 s, reconecta.
      probeAliveness();
    } else if (
      ws.readyState === WebSocket.CONNECTING &&
      Date.now() - connectStartedAtRef.current > CONNECT_TIMEOUT_MS
    ) {
      // CONNECTING más viejo que el plazo del watchdog: el intento quedó
      // congelado (los timers no corren durante un freeze, así que el watchdog
      // pudo no disparar nunca). Descartarlo y discar ya. Un CONNECTING joven
      // se deja en paz: es un intento legítimo en curso y su watchdog vive.
      wsLog("stale CONNECTING on revive — discarding", {
        ageMs: Date.now() - connectStartedAtRef.current,
      });
      clearTimeout(reconnectTimerRef.current);
      discardSocket(ws);
      reconnectAttemptRef.current = 0;
      connect();
    }
  }, [connect, probeAliveness]);

  // Camino compartido de "la app/pestaña volvió al frente": lo usan tanto
  // visibilitychange→visible como appStateChange(isActive) de Capacitor (en
  // MIUI el segundo puede ser el ÚNICO que llegue tras un freeze largo).
  // Idempotente: si ambos llegan seguidos, el segundo encuentra la ausencia ya
  // consumida y el dedup de connect/probe hace el resto.
  const handleAppReturn = useCallback(() => {
    // Drenar YA el buffer acumulado. El flush pendiente quedó como timer
    // (posiblemente estrangulado a ~1/min) y sin este drenaje el usuario
    // miraría marcadores congelados hasta que ese timer atrasado dispare.
    cancelPendingFlush();
    flushPendingMessages();

    // Congelar la ausencia AL VOLVER (ver lastAbsenceMsRef).
    const hiddenAt = tabHiddenAtRef.current;
    const absenceMs = hiddenAt == null ? null : Date.now() - hiddenAt;
    if (hiddenAt != null) {
      lastAbsenceMsRef.current = absenceMs;
      tabHiddenAtRef.current = null;
    }
    const socketWasAlive = wsRef.current?.readyState === WebSocket.OPEN;

    // Socket sano ⇒ checkAndRevive no reconecta ⇒ no habrá onopen ⇒ nadie
    // dispara el resync. Pero NO se dispara acá mismo: tras un freeze largo el
    // kernel del teléfono puede tener minutos de frames encolados que se van a
    // entregar DESPUÉS de este instante — un resync disparado ahora capturaría
    // un startTs anterior a esa entrega, los frames viejos se versionarían
    // después de él y el merge preservaría estado viejo descartando el
    // snapshot HTTP fresco. Se arma la BARRERA DE PONG: el pong de un ping
    // POSTERIOR a este instante viaja DETRÁS de todo ese backlog (stream
    // FIFO), así que su llegada prueba que el backlog drenó — recién ahí se
    // dispara el tab-return (ver ws.onmessage). Si el pong no llega, el probe
    // fuerza reconexión y el onopen dispara su propio resync — la barrera se
    // limpia en fireResyncHandlers.
    if (
      socketWasAlive &&
      absenceMs != null &&
      absenceMs > SYNC_ABSENCE_THRESHOLD_MS
    ) {
      // Descartar un probe armado ANTES del freeze: su pong llega DELANTE del
      // backlog y no sirve como barrera; con el slot libre, el probeAliveness
      // de checkAndRevive manda un ping nuevo cuyo eco sí califica.
      clearPingTimeout();
      pendingTabReturnSinceRef.current = Date.now();
    }

    checkAndRevive();
  }, [cancelPendingFlush, flushPendingMessages, checkAndRevive]);

  // ─── Visibility change: force immediate reconnect when tab returns ────────
  // Browsers aggressively throttle/suspend background tabs, which can kill
  // the TCP connection silently. When the user returns, don't wait for the
  // reconnect timer — check immediately and reconnect if needed.
  // Also covers BFCache restores (pageshow persisted) and the Page Lifecycle
  // "resume" event: neither guarantees a visibilitychange, but both mean the
  // page just came back from a state where the socket may have been killed.
  useEffect(() => {
    const handleVisibility = () => {
      wsLog("visibilitychange", { state: document.visibilityState, readyState: wsRef.current?.readyState ?? null });
      if (document.visibilityState === "hidden") {
        tabHiddenAtRef.current = Date.now();
        // Un flush programado con rAF no dispara en background: degradarlo a
        // timeout para que el buffer no retenga mensajes toda la ausencia.
        if (flushHandleRef.current?.raf != null) {
          cancelPendingFlush();
          schedulePendingFlush();
        }
        return;
      }
      handleAppReturn();
    };
    // freeze/pagehide(BFCache) pueden llegar SIN visibilitychange:hidden
    // previo (issue #540): estampar el inicio de la ausencia para que el
    // resync de la vuelta pueda evaluarla. Si hidden ya estampó este ciclo,
    // no pisar — la marca más temprana es la real.
    const stampAbsenceStart = () => {
      if (tabHiddenAtRef.current == null) tabHiddenAtRef.current = Date.now();
    };
    const handleFreeze = () => {
      wsLog("page lifecycle freeze");
      stampAbsenceStart();
    };
    const handlePageshow = (event) => {
      if (event.persisted) {
        wsLog("pageshow persisted=true — BFCache restore");
        checkAndRevive();
      }
    };
    const handlePagehide = (event) => {
      // Solo la variante BFCache (persisted): un pagehide de navegación real
      // destruye la página y no hay vuelta que medir.
      if (event.persisted) stampAbsenceStart();
    };
    const handleResume = () => {
      wsLog("page lifecycle resume");
      checkAndRevive();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pageshow", handlePageshow);
    window.addEventListener("pagehide", handlePagehide);
    // Page Lifecycle API — soporte parcial (Chrome sí); additivo e inocuo donde no exista.
    document.addEventListener("resume", handleResume);
    document.addEventListener("freeze", handleFreeze);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pageshow", handlePageshow);
      window.removeEventListener("pagehide", handlePagehide);
      document.removeEventListener("resume", handleResume);
      document.removeEventListener("freeze", handleFreeze);
      tabHiddenAtRef.current = null;
      lastAbsenceMsRef.current = null;
      pendingTabReturnSinceRef.current = null;
    };
  }, [
    handleAppReturn,
    checkAndRevive,
    cancelPendingFlush,
    schedulePendingFlush,
  ]);

  // ─── Conciencia de red ─────────────────────────────────────────────────────
  // Al volver la conectividad se reconecta EN EL ACTO en vez de esperar el
  // próximo escalón del backoff (hasta 30 s mirando el banner con la red ya
  // sana). Y un cambio de interfaz (wifi↔datos) puede matar el TCP sin FIN
  // aunque el socket figure OPEN: se prueba con ping en vez de asumir.
  useEffect(() => {
    const handleOnline = () => {
      wsLog("network online", { readyState: wsRef.current?.readyState ?? null });
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        probeAliveness();
        return;
      }
      if (ws && ws.readyState === WebSocket.CONNECTING) {
        const ageMs = Date.now() - connectStartedAtRef.current;
        // Ver ONLINE_DISCARD_CONNECTING_AGE_MS: un dial fresco puede estar
        // discando ya contra la red nueva; solo se descarta el viejo.
        if (ageMs <= ONLINE_DISCARD_CONNECTING_AGE_MS) return;
        discardSocket(ws);
      }
      clearTimeout(reconnectTimerRef.current);
      reconnectAttemptRef.current = 0;
      connect();
    };
    const handleOffline = () => {
      wsLog("network offline", {});
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [connect, probeAliveness]);

  // ─── App nativa (Capacitor): ciclo de vida del OS ──────────────────────────
  // En Android el WebView normalmente también dispara visibilitychange, pero
  // en los OEM agresivos (MIUI) la entrega tras un freeze largo no está
  // garantizada — appStateChange es la señal nativa y hace de redundancia,
  // con el MISMO camino de vuelta (handleAppReturn: drena, congela ausencia,
  // revive y arma el resync) para que ninguna de las dos rutas se quede corta.
  // En web isNative() es false y el plugin ni se descarga (import dinámico).
  useEffect(() => {
    if (!isNative()) return undefined;
    let handle = null;
    let cancelled = false;
    import("@capacitor/app")
      .then(({ App: CapacitorApp }) =>
        CapacitorApp.addListener("appStateChange", ({ isActive }) => {
          wsLog("appStateChange", { isActive });
          if (isActive) {
            handleAppReturn();
          } else if (tabHiddenAtRef.current == null) {
            // Registrar el inicio de la ausencia si visibilitychange no llegó.
            tabHiddenAtRef.current = Date.now();
          }
        }),
      )
      .then((h) => {
        if (cancelled) h?.remove?.();
        else handle = h;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      handle?.remove?.();
    };
  }, [handleAppReturn]);

  // Reintento manual (banner "Reintentar"): descarta cualquier socket o timer
  // en curso, resetea el backoff, rehabilita el kick de refresh y disca ya —
  // salteando el gate de offline (si navigator.onLine miente, este gesto es la
  // única salida). Es el escape hatch de la app nativa, donde recargar la
  // página no existe.
  const forceReconnect = useCallback(() => {
    const ws = wsRef.current;
    // Un dial en vuelo y fresco YA está haciendo lo que el usuario pide:
    // abortarlo con cada tap solo reinicia el handshake (en una red lenta, un
    // tap por segundo garantiza no conectar nunca). El CONNECTING viejo
    // (congelado) sí se descarta abajo.
    if (
      ws &&
      ws.readyState === WebSocket.CONNECTING &&
      Date.now() - connectStartedAtRef.current < CONNECT_TIMEOUT_MS
    ) {
      wsLog("forceReconnect ignored — dial already in flight", {});
      return;
    }
    wsLog("forceReconnect (user gesture)", {
      readyState: ws?.readyState ?? null,
    });
    clearTimeout(reconnectTimerRef.current);
    clearPingTimeout();
    lastRefreshKickRef.current = 0;
    if (ws) discardSocket(ws);
    else clearConnectWatchdog();
    setConnected(false);
    reconnectAttemptRef.current = 0;
    connect({ ignoreOffline: true });
  }, [connect]);

  // Lectura del estado REAL del socket (no del flag connected, que puede
  // quedar stale tras una race). El banner offline la usa como doble check.
  const isSocketOpen = useCallback(
    () => wsRef.current?.readyState === WebSocket.OPEN,
    [],
  );

  const value = useMemo(
    () => ({
      subscribe,
      unsubscribe,
      onReconnect,
      offReconnect,
      sendMessage,
      connected,
      getAndConsumeLastAbsenceMs,
      isSocketOpen,
      forceReconnect,
    }),
    [
      subscribe,
      unsubscribe,
      onReconnect,
      offReconnect,
      sendMessage,
      connected,
      getAndConsumeLastAbsenceMs,
      isSocketOpen,
      forceReconnect,
    ],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  const ctx = useContext(WebSocketContext);
  if (!ctx)
    throw new Error(
      "useWebSocketContext must be used inside <WebSocketProvider>",
    );
  return ctx;
}

/**
 * Igual, pero devuelve `null` en vez de tirar cuando no hay provider.
 *
 * Para lo que solo DECORA: un contador en un badge de navegación no puede
 * tumbar la pantalla, ni obligar a montar el provider a quien renderice el rail
 * suelto. Quien dependa del socket para funcionar sigue usando el que tira.
 */
export function useOptionalWebSocketContext() {
  return useContext(WebSocketContext);
}
